<template>
  <UModal 
    v-model:open="isOpen"
  >
    <div @click="openModal" class="w-full">
      <slot>
        <UiRippleButton class="brown w-full">
          Request Custom Quote
        </UiRippleButton>
      </slot>
    </div>

    <template #content="{ close }">
      <div class="p-6 md:p-8 relative">
        <!-- Close button -->
        <button 
          @click="close" 
          class="absolute top-4 right-4 p-2 rounded-full border "
          aria-label="Close modal"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div v-if="!formSubmitted">
          <h3 class="text-2xl md:text-3xl font-semibold mb-4">Request a Custom Wheelset Quote</h3>
          <p class="text-gray-600 mb-6">
            Fill out your contact information below and we'll send you a personalized quote.
          </p>

          <form @submit.prevent="submitForm" class="space-y-4">
            <!-- Customer Information -->
            <div>
              <h4 class="text-lg font-semibold mb-3">Your Information</h4>
              
              <div>
                <label for="name" class="block text-sm font-semibold mb-2">Name</label>
                <input 
                  v-model="formData.name" 
                  type="text" 
                  id="name"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gravaadarkbrown"
                  :class="{ 'border-red-500': errors.name }"
                />
                <p v-if="errors.name" class="text-red-500 text-sm mt-1">{{ errors.name }}</p>
              </div>

              <div class="mt-3">
                <label for="email" class="block text-sm font-semibold mb-2">Email</label>
                <input 
                  v-model="formData.email" 
                  type="email" 
                  id="email"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gravaadarkbrown"
                  :class="{ 'border-red-500': errors.email }"
                />
                <p v-if="errors.email" class="text-red-500 text-sm mt-1">{{ errors.email }}</p>
              </div>

              <div class="mt-3">
                <label for="address" class="block text-sm font-semibold mb-2">Address</label>
                <textarea 
                  v-model="formData.address" 
                  id="address"
                  rows="3"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gravaadarkbrown resize-y"
                  :class="{ 'border-red-500': errors.address }"
                ></textarea>
                <p v-if="errors.address" class="text-red-500 text-sm mt-1">{{ errors.address }}</p>
              </div>
            </div>

            <div class="flex justify-end pt-4">
              <UiRippleButton 
                type="submit" 
                class="brown"
                :disabled="isSubmitting"
              >
                <span v-if="!isSubmitting" class="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
                  </svg>
                  Send Quote Request
                </span>
                <span v-else>Sending...</span>
              </UiRippleButton>
            </div>

            <p v-if="errors.submit" class="text-red-500 text-sm text-center">{{ errors.submit }}</p>
          </form>
        </div>

        <div v-else class="text-center py-8">
          <h3 class="text-2xl md:text-3xl font-semibold mb-4">Quote Request Sent!</h3>
          <p class="text-gray-600 mb-6">
            Thank you for your interest in our custom wheelsets!<br/>
            We'll review your specifications and send you a personalized quote shortly.
          </p>
          
          <UiRippleButton class="brown" @click="isOpen = false">
            Close
          </UiRippleButton>
        </div>
      </div>
    </template>
  </UModal>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'

const props = defineProps({
  product: {
    type: Object,
    required: true
  },
  currentVariant: {
    type: Object,
    default: null
  },
  customConfig: {
    type: Object,
    required: true
  }
})

const isOpen = ref(false)
const formSubmitted = ref(false)
const isSubmitting = ref(false)

const formData = reactive({
  name: '',
  email: '',
  address: ''
})

const errors = reactive({
  name: '',
  email: '',
  address: '',
  submit: ''
})

// Get selected variant options
const selectedVariantOptions = computed(() => {
  if (!props.currentVariant?.node?.selectedOptions) {
    return {
      freehubBody: '',
      discRotorFront: '',
      discRotorRear: ''
    }
  }
  
  const options = {}
  props.currentVariant.node.selectedOptions.forEach(option => {
    if (option.name === 'Freehub Body') {
      options.freehubBody = option.value
    } else if (option.name === 'Disc Brake Rotor Front') {
      options.discRotorFront = option.value
    } else if (option.name === 'Disc Brake Rotor Rear') {
      options.discRotorRear = option.value
    }
  })
  
  return options
})

const openModal = () => {
  isOpen.value = true
  // Reset form state when opening (if previously submitted)
  if (formSubmitted.value) {
    formSubmitted.value = false
    Object.keys(formData).forEach(key => {
      formData[key] = ''
    })
    Object.keys(errors).forEach(key => {
      errors[key] = ''
    })
  }
}

// Watch for modal closing and reset form data (but keep formSubmitted state)
watch(isOpen, (newValue, oldValue) => {
  if (oldValue === true && newValue === false) {
    // Modal just closed, reset form data after a delay (but NOT formSubmitted)
    setTimeout(() => {
      Object.keys(formData).forEach(key => {
        formData[key] = ''
      })
      Object.keys(errors).forEach(key => {
        errors[key] = ''
      })
    }, 300)
  }
})

// Email validation
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// Form validation
const validateForm = () => {
  let isValid = true
  
  // Reset errors
  errors.name = ''
  errors.email = ''
  errors.address = ''
  errors.submit = ''

  // Validate name
  if (!formData.name.trim()) {
    errors.name = 'Please enter your name'
    isValid = false
  }

  // Validate email
  if (!formData.email.trim()) {
    errors.email = 'Please enter your email'
    isValid = false
  } else if (!isValidEmail(formData.email)) {
    errors.email = 'Please enter a valid email address'
    isValid = false
  }

  // Validate address
  if (!formData.address.trim()) {
    errors.address = 'Please enter your address'
    isValid = false
  }

  return isValid
}

// Submit form
const submitForm = async () => {
  if (!validateForm()) {
    return
  }

  isSubmitting.value = true
  errors.submit = ''

  try {
    const response = await $fetch('/api/send-wheelset-quote', {
      method: 'POST',
      body: {
        productTitle: props.product.title,
        variantTitle: props.currentVariant?.node?.title || '',
        name: formData.name,
        email: formData.email,
        address: formData.address,
        freehubBody: selectedVariantOptions.value.freehubBody,
        discRotorFront: selectedVariantOptions.value.discRotorFront,
        discRotorRear: selectedVariantOptions.value.discRotorRear,
        riderWeight: props.customConfig.riderWeight,
        rimType: props.customConfig.rimType,
        rimHeight: props.customConfig.rimHeight,
        tireWidth: props.customConfig.tireWidth,
        rimBrand: props.customConfig.rimBrand,
        otherRequests: props.customConfig.otherRequests
      }
    })

    if (response.success) {
      formSubmitted.value = true
    } else {
      errors.submit = response.error || 'Failed to send quote request. Please try again.'
    }
  } catch (error) {
    console.error('Error submitting form:', error)
    errors.submit = 'An error occurred. Please try again later.'
  } finally {
    isSubmitting.value = false
  }
}
</script>
